import React, { useState, useEffect, useRef, useMemo, Suspense, lazy } from 'react';
import axios from 'axios';
import { getCachedSvg, cacheSvg, buildCacheKey, postSvgCacheToServer, subscribeCacheUpdate } from '../../utils/tikzSvgCache';
import { prerenderTikzList } from '../../utils/tikzPrerender';
import { renderTikzToSvg } from '../../utils/tikzRender';
import { renderTikzToSvgWithFallback } from '../../utils/diagramEditor';
import OneQuestionAtATime from '../child/OneQuestionAtATime';
import { truncateTaskTitle } from '../../utils/formatters';
import useDarkMode from '../../hooks/useDarkMode';
const DiagramVisualEditor = lazy(() => import('./DiagramVisualEditor'));
const VideoExcalidrawModal = lazy(() => import('../video/VideoExcalidrawModal'));

// Adds an optional math-keyboard toggle next to each free-text worksheet
// answer field (short answer / long answer / fill-in-the-blank). The
// original <input>/<textarea> stays in the DOM and remains the source of
// truth for existing answer-capture listeners and answer-key population;
// the <math-field> just mirrors its value and forwards a real 'input' event
// on the original element so nothing else in this component needs to know
// about it. `listeners` is the same Map the caller uses for effect cleanup.
const setupMathToggles = (container, listeners) => {
  const fields = container.querySelectorAll(
    '.answer-input input[type="text"]:not([data-math-toggle]), .answer-input textarea:not([data-math-toggle])'
  );

  fields.forEach((input) => {
    input.setAttribute('data-math-toggle', 'attached');

    const wrapper = document.createElement('span');
    wrapper.className = 'math-toggle-wrapper';
    input.parentNode.insertBefore(wrapper, input);
    wrapper.appendChild(input);

    const toggleBtn = document.createElement('button');
    toggleBtn.type = 'button';
    toggleBtn.className = 'math-toggle-btn';
    toggleBtn.title = 'Enter a math equation';
    toggleBtn.setAttribute('aria-label', 'Toggle math keyboard');
    toggleBtn.textContent = 'Σ';
    wrapper.appendChild(toggleBtn);

    let mathField = null;
    let isMathMode = false;

    const clickHandler = async () => {
      if (!isMathMode) {
        if (!window.customElements.get('math-field')) {
          toggleBtn.disabled = true;
          await import('mathlive');
          toggleBtn.disabled = false;
        }
        if (!mathField) {
          mathField = document.createElement('math-field');
          mathField.className = 'math-toggle-field';
          mathField.setAttribute('math-virtual-keyboard-policy', 'auto');
          const mathInputHandler = () => {
            input.value = mathField.value;
            input.dispatchEvent(new Event('input', { bubbles: true }));
          };
          mathField.addEventListener('input', mathInputHandler);
          listeners.set(mathField, { event: 'input', handler: mathInputHandler });
          wrapper.insertBefore(mathField, toggleBtn);
        }
        mathField.value = input.value;
        input.style.display = 'none';
        mathField.style.display = '';
        toggleBtn.classList.add('active');
        isMathMode = true;
      } else {
        input.style.display = '';
        if (mathField) mathField.style.display = 'none';
        toggleBtn.classList.remove('active');
        isMathMode = false;
      }
    };

    toggleBtn.addEventListener('click', clickHandler);
    listeners.set(toggleBtn, { event: 'click', handler: clickHandler });
  });
};

const normalizeWorksheetQuestionType = (sectionType = '') => {
  if (!sectionType) return 'Multiple Choice';
  if (sectionType.includes('Multiple Choice')) return 'Multiple Choice';
  if (sectionType.includes('Fill in the Blank')) return 'Fill in the Blanks';
  if (sectionType.includes('True') || sectionType.includes('False')) return 'True False';
  if (sectionType.includes('Assertion')) return 'Assertion Reasoning';
  if (sectionType.includes('Short Answer')) return 'Short Answer';
  if (sectionType.includes('Long Answer')) return 'Long Answer';
  return sectionType;
};

const extractEditableQuestions = (worksheet) => {
  if (!worksheet) return [];

  let jsonData = worksheet.json_data;
  if (typeof jsonData === 'string') {
    try {
      jsonData = JSON.parse(jsonData);
    } catch (error) {
      console.error('extractEditableQuestions: failed to parse worksheet JSON:', error);
      return [];
    }
  }

  if (!jsonData || !Array.isArray(jsonData.questions)) return [];

  const flattened = [];

  jsonData.questions.forEach((section) => {
    const mappedType = normalizeWorksheetQuestionType(section?.question_type);
    const questionsList = section?.questions_list || section?.questions || [];

    questionsList.forEach((q) => {
      let questionOptions = q.options || [];
      if (mappedType === 'True False') {
        questionOptions = ['True', 'False'];
      } else if (
        mappedType === 'Short Answer' ||
        mappedType === 'Long Answer' ||
        mappedType === 'Fill in the Blanks' ||
        mappedType === 'Assertion Reasoning'
      ) {
        questionOptions = [];
      }

      flattened.push({
        question_number: q.question_number || flattened.length + 1,
        question_type: mappedType,
        question_text: q.question_text || q.question || q.text || '',
        options: questionOptions,
        correct_answer: q.correct_answer || '',
        hint: q.hint || '',
        marks: parseInt(q.marks || 1, 10),
        diagram: q.diagram || null,
        concept: q.concept || '',
        skill: q.skill || '',
        difficulty: q.difficulty_level || q.difficulty || 'medium',
        topic: q.topic || ''
      });
    });
  });

  if (Array.isArray(jsonData.answer_key)) {
    jsonData.answer_key.forEach((ans) => {
      const idx = flattened.findIndex((q) => q.question_number === ans.question_number);
      if (idx !== -1) {
        flattened[idx].correct_answer =
          ans.answer_text || ans.answer || flattened[idx].correct_answer || '';
      }
    });
  }

  return flattened;
};

const sanitizeTikzCode = (value = '') => String(value).replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '');

const sanitizeTikzScriptsInHtml = (htmlContent = '') =>
  String(htmlContent).replace(
    /<script([^>]*)type=(?:"text\/tikz"|'text\/tikz')([^>]*)>([\s\S]*?)<\/script>/gi,
    (_match, beforeAttrs = '', afterAttrs = '', tikzCode = '') =>
      `<script${beforeAttrs}type="text/tikz"${afterAttrs}>${sanitizeTikzCode(tikzCode)}</script>`,
  );

// Small badges injected into the rendered HTML so users can see whether a
// diagram is live TikZ or a cached SVG.
const DIAGRAM_BADGE_STYLES = `
.diagram { display: block; margin: 0.5rem 0; text-align: left; }
.diagram-type-badge {
  display: block;
  width: fit-content;
  padding: 2px 8px;
  margin-bottom: 6px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: 4px;
  border: 1px solid;
  font-family: Arial, sans-serif;
  line-height: 1.4;
}
.diagram-type-badge.tikz { background: #dbeafe; color: #1d4ed8; border-color: #93c5fd; }
.diagram-type-badge.svg-cache { background: #f3e8ff; color: #7e22ce; border-color: #d8b4fe; }
.diagram-type-badge.svg-rendered { background: #ccfbf1; color: #0f766e; border-color: #5eead4; }
.diagram-type-badge.svg { background: #d1fae5; color: #047857; border-color: #6ee7b7; }
.tikz-view-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin: 0.5rem 0;
  border-radius: 8px;
  background: #eff6ff;
  border: 1px dashed #93c5fd;
  color: #1e3a8a;
  font-family: Arial, sans-serif;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
}
.tikz-view-btn:hover { background: #dbeafe; }
.tikz-view-btn:focus { outline: 2px solid #3b82f6; outline-offset: 2px; }
.tikz-view-btn .tikz-view-cta { font-weight: 500; }
.tikz-view-btn .diagram-type-badge { margin-bottom: 0; }
.tikz-view-btn[disabled],
.tikz-view-btn--rendering {
  cursor: progress;
  background: #f1f5f9;
  border-style: solid;
  border-color: #cbd5e1;
  color: #475569;
}
.tikz-view-btn[disabled]:hover,
.tikz-view-btn--rendering:hover { background: #f1f5f9; }
.diagram,
.tikz-rendered,
.tikz-cached {
  position: relative;
  max-width: 100%;
  overflow: auto;
  contain: layout paint;
}

.diagram svg,
.tikz-rendered svg,
.tikz-cached svg {
  display: block !important;
  max-width: 100% !important;
  height: auto !important;
  margin: 0 auto !important;
  position: static !important;
  inset: auto !important;
  transform: none !important;
}
`;

// Tag every diagram in the worksheet HTML with a small label so users can see
// at a glance whether it's a live TikZ compile or a server-rendered SVG. The
// backend's HTML generator wraps each diagram in a <div class="diagram"> — we
// just insert a badge after the opening tag, picking the type from what
// follows (a <script type="text/tikz"> still needs TikZJax; an <svg> is
// already rendered server-side).
const labelDiagramsInHtml = (htmlContent = '') => {
  let result = String(htmlContent);
  // 1) Backend-rendered SVG inside <div class="diagram">: add "SVG (rendered)".
  result = result.replace(
    /(<div\b[^>]*\bclass=(?:"[^"]*\bdiagram\b[^"]*"|'[^']*\bdiagram\b[^']*')[^>]*>)(\s*)(<svg[\s>])/gi,
    (_match, openTag, ws, svgOpen) =>
      `${openTag}${ws}<span class="diagram-type-badge svg-rendered">SVG (rendered)</span>${svgOpen}`,
  );
  // 2) Uncached TikZ <script>: wrap so TikZJax still finds and processes it
  // and a "TikZ" badge appears above the eventual SVG.
  result = result.replace(
    /<script([^>]*)type=(?:"text\/tikz"|'text\/tikz')([^>]*)>([\s\S]*?)<\/script>/gi,
    (match) =>
      `<div class="diagram tikz-live"><span class="diagram-type-badge tikz">TikZ</span>${match}</div>`,
  );
  return result;
};

const EnhancedWorksheetDisplay = ({
  worksheet,
  showAnswerKey = false,
  className = "",
  mode = "online",
  onAnswersChange = null,
  initialAnswers = {},
  onWorksheetUpdated = null,
  canEditDiagram = false,
  enableOneQuestionToggle = true,
  canEditQuestions = true,
  onViewModeChange = null,
}) => {
  console.log('📄 EnhancedWorksheetDisplay mounted/updated');
  console.log('📄 Worksheet ID:', worksheet?.id);
  console.log('📄 HTML content length:', worksheet?.html_content?.length);
  console.log('📄 Num questions:', worksheet?.num_questions);
  console.log('📄 Is modified:', worksheet?.is_modified);
  console.log('📄 Reload timestamp:', worksheet?._reloadTimestamp);
  
  const [localWorksheet, setLocalWorksheet] = useState(worksheet);
  const [diagramEditor, setDiagramEditor] = useState({
    isOpen: false,
    questionId: null,
    tikzCode: '',
    originalTikzCode: '',
    svgCode: '',
    originalSvgCode: '',
    editorMode: 'tikz',
    isIframe: false,
    doc: null,
    excalidrawOpen: false,
    excalidrawElements: null
  });
  const [diagramEditorError, setDiagramEditorError] = useState('');
  const [isSavingDiagram, setIsSavingDiagram] = useState(false);

  const [oneQuestionMode, setOneQuestionMode] = useState(() => {
    if (!enableOneQuestionToggle) return false;
    // Default to Show All (EnhancedWorksheetDisplay) when the worksheet has
    // TikZ diagrams so the iframe renderer can display them without breaking
    // the question pager. Otherwise default to OneQuestionAtATime.
    const html = worksheet?.html_content || '';
    return !(html.includes('type="text/tikz"') || html.includes("type='text/tikz'"));
  });
  // Pre-populate when starting in one-question mode so OneQuestionAtATime never
  // mounts with an empty overrideQuestions list (mirrors handleToggleOneQuestionMode).
  const [editableQuestions, setEditableQuestions] = useState(() => {
    if (!enableOneQuestionToggle) return [];
    const html = worksheet?.html_content || '';
    const hasTikZInHtml = html.includes('type="text/tikz"') || html.includes("type='text/tikz'");
    // Don't pre-populate when starting in Show All mode (TikZ worksheets).
    return hasTikZInHtml ? [] : extractEditableQuestions(worksheet);
  });
  const [questionsSaving, setQuestionsSaving] = useState(false);
  const [questionsSaveError, setQuestionsSaveError] = useState('');
  const [questionsSaveSuccess, setQuestionsSaveSuccess] = useState('');

  useEffect(() => {
    setLocalWorksheet(worksheet);
  }, [worksheet?.id, worksheet?._reloadTimestamp]);

  const activeWorksheet = localWorksheet || worksheet;
  const truncatedWorksheetTitle = useMemo(
    () => truncateTaskTitle(activeWorksheet?.title || '', 30, { fallback: '', suffix: '…' }),
    [activeWorksheet?.title],
  );

  const sanitizedWorksheetHtmlContent = useMemo(
    () => sanitizeTikzScriptsInHtml(activeWorksheet?.html_content || ''),
    [activeWorksheet?.html_content],
  );

  // Bumped whenever the local TikZ→SVG cache is updated (foreground render
  // in a tile or background prerender below). Threaded into the memos that
  // build the worksheet HTML so a freshly cached diagram swaps into the
  // displayed content without requiring a worksheet reload.
  const [tikzCacheTick, setTikzCacheTick] = useState(0);
  // Set of viewer-diagram indices currently being rendered in the
  // background after a user click. Tracked in React state so the button's
  // "Rendering…" label survives the worksheet-HTML re-renders that fire
  // every time the cache changes; without it, a click on diagram B while
  // diagram A is still compiling would revert B's button to "View Diagram"
  // the moment A landed in cache. Declared here (rather than next to the
  // other UI state below) because tikzCachedWorksheetContent and
  // worksheetRenderKey both read it during render — moving it later would
  // hit the temporal dead zone.
  const [renderingIndices, setRenderingIndices] = useState(() => new Set());
  useEffect(() => {
    const unsubscribe = subscribeCacheUpdate(() => {
      setTikzCacheTick((tick) => tick + 1);
    });
    return unsubscribe;
  }, []);

  // Clear the in-flight render set whenever the worksheet identity changes
  // — viewer-diagram indices are recomputed from the new HTML and a stale
  // "Rendering…" state would otherwise stick on whichever button now
  // happens to share an index with a prior click.
  useEffect(() => {
    setRenderingIndices(new Set());
  }, [activeWorksheet?.id, activeWorksheet?._reloadTimestamp]);

  // Walk the server-rendered worksheet HTML and replace every TikZ <script>
  // with either an inline SVG (if a cached render is available, either
  // attached server-side or in this browser's localStorage) or a "View
  // Diagram" button placeholder. The page itself never runs TikZJax — a
  // failed render in the worksheet view always destabilised the surrounding
  // dialog; rendering only happens inside the dedicated viewer modal that
  // the placeholder opens on click.
  const tikzCachedWorksheetContent = useMemo(() => {
    const empty = { html: sanitizedWorksheetHtmlContent || '', allCached: false, viewerDiagrams: [] };
    if (!sanitizedWorksheetHtmlContent || typeof window === 'undefined') {
      return empty;
    }

    // Build a hash → svg_cache map from the worksheet's json_data so that any
    // TikZ <script> still embedded in html_content can be swapped for the
    // server-rendered SVG. The hash format matches the backend's
    // build_tikz_cache_key so the lookup is exact, no string fuzziness.
    const svgByCacheHash = new Map();
    const collectDiagram = (diagram) => {
      if (
        diagram
        && typeof diagram === 'object'
        && typeof diagram.svg_cache === 'string'
        && diagram.svg_cache.trim()
        && typeof diagram.svg_cache_hash === 'string'
        && diagram.svg_cache_hash
      ) {
        svgByCacheHash.set(diagram.svg_cache_hash, diagram.svg_cache);
      }
    };
    try {
      let json = activeWorksheet?.json_data;
      if (typeof json === 'string') json = JSON.parse(json);
      if (json && Array.isArray(json.questions)) {
        json.questions.forEach((section) => {
          (section?.questions_list || []).forEach((q) => collectDiagram(q?.diagram));
        });
      }
      if (json && Array.isArray(json.answer_key)) {
        json.answer_key.forEach((entry) => collectDiagram(entry?.diagram));
      }
    } catch (_error) {
      // json_data malformed — fall back to localStorage cache only.
    }

    let hasScripts = false;
    let allCached = true;
    let tikzIndex = 0;
    const viewerDiagrams = [];
    const replaced = sanitizedWorksheetHtmlContent.replace(
      /<script([^>]*)type=(?:"text\/tikz"|'text\/tikz')([^>]*)>([\s\S]*?)<\/script>/gi,
      (_match, _beforeAttrs = '', _afterAttrs = '', tikzCode = '') => {
        hasScripts = true;
        const hashKey = buildCacheKey(tikzCode);
        const serverSvg = svgByCacheHash.get(hashKey);
        if (serverSvg) {
        const wrapper = `
          <div class="diagram tikz-rendered" data-tikz-cache-key="${hashKey}" data-tikz-cache-index="${tikzIndex}">
            <span class="diagram-type-badge svg-rendered">SVG (rendered)</span>
            <div class="diagram-svg-box">${serverSvg}</div>
          </div>
        `;
          tikzIndex += 1;
          return wrapper;
        }
        const cachedSvg = getCachedSvg(tikzCode);
        if (cachedSvg) {
          const wrapper = `
            <div class="diagram tikz-cached" data-tikz-cache-key="${hashKey}" data-tikz-cache-index="${tikzIndex}">
              <div class="diagram-svg-box">${cachedSvg}</div>
            </div>
          `;
          tikzIndex += 1;
          return wrapper;
        }
        // No cached SVG — emit a button whose click triggers a background
        // render rather than opening a modal. The parent React tree owns
        // the diagram code via viewerDiagrams; the button only carries a
        // numeric index so escaping pitfalls in data-attributes are
        // avoided. When the user already clicked, render in the inline
        // "Rendering…" variant so the click feedback survives the HTML
        // re-renders triggered by other diagrams landing in cache.
        allCached = false;
        const buttonIndex = viewerDiagrams.length;
        viewerDiagrams.push({ index: buttonIndex, tikzCode, hash: hashKey });
        const isRendering = renderingIndices.has(buttonIndex);
        const button = isRendering
          ? `<button type="button" class="diagram tikz-view-btn tikz-view-btn--rendering" data-tikz-view-index="${buttonIndex}" disabled aria-busy="true"><span class="diagram-type-badge tikz">TikZ</span><span class="tikz-view-cta">⏳ Rendering…</span></button>`
          : `<button type="button" class="diagram tikz-view-btn" data-tikz-view-index="${buttonIndex}"><span class="diagram-type-badge tikz">TikZ</span><span class="tikz-view-cta">📐 View Diagram</span></button>`;
        tikzIndex += 1;
        return button;
      },
    );

    if (!hasScripts) {
      return empty;
    }

    return { html: replaced, allCached, viewerDiagrams };
  }, [sanitizedWorksheetHtmlContent, activeWorksheet?.json_data, tikzCacheTick, renderingIndices]);

  const worksheetRenderKey = useMemo(() => {
    if (!activeWorksheet) return 'no-worksheet';

    const html = sanitizedWorksheetHtmlContent;
    let hash = 0;
    for (let index = 0; index < html.length; index += 1) {
      hash = ((hash << 5) - hash) + html.charCodeAt(index);
      hash |= 0;
    }

    return `${activeWorksheet.id || 'unknown'}-${activeWorksheet._reloadTimestamp ?? 'initial'}-${html.length}-${hash}-${tikzCacheTick}-r${renderingIndices.size}-${Array.from(renderingIndices).sort((a, b) => a - b).join(',')}`;
  }, [activeWorksheet?.id, activeWorksheet?._reloadTimestamp, sanitizedWorksheetHtmlContent, tikzCacheTick, renderingIndices]);

  // Check if HTML content is empty or too short
  if (!sanitizedWorksheetHtmlContent || sanitizedWorksheetHtmlContent.length < 100) {
    console.error('⚠️ WARNING: HTML content is missing or too short!');
    console.error('⚠️ HTML length:', sanitizedWorksheetHtmlContent?.length);
  }
  
  const [isKaTeXLoaded, setIsKaTeXLoaded] = useState(false);
  const [isTikZJaxLoaded, setIsTikZJaxLoaded] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [hasTikZ, setHasTikZ] = useState(null); // null = not checked yet, false = no TikZ, true = has TikZ
  const [isContentReady, setIsContentReady] = useState(false);
  const iframeRef = useRef(null);
  const iframeResizeCleanupRef = useRef(null);
  const hydratedAnswersRef = useRef(null);
  const { isDarkMode } = useDarkMode();

  // Mirror the parent document's `.dark` class on the iframe so the
  // injected dark-mode CSS in the worksheet HTML responds to toggles
  // without re-writing the whole document.
  useEffect(() => {
    const iframeEl = iframeRef.current;
    if (!iframeEl) return;
    try {
      const iframeDoc = iframeEl.contentDocument || iframeEl.contentWindow?.document;
      if (!iframeDoc) return;
      iframeDoc.documentElement?.classList.toggle('dark', isDarkMode);
      iframeDoc.body?.classList.toggle('dark', isDarkMode);
    } catch (_) {
      /* cross-origin or not yet ready — the iframe write itself injects the right class */
    }
  }, [isDarkMode]);

  const derivedAnswerKey = useMemo(() => {
    if (!activeWorksheet?.json_data) return {};

    let jsonData = activeWorksheet.json_data;
    if (typeof jsonData === 'string') {
      try {
        jsonData = JSON.parse(jsonData);
      } catch (error) {
        console.error('Error parsing worksheet json_data for answer key:', error);
        return {};
      }
    }

    const answerMap = {};

    if (Array.isArray(jsonData?.answer_key)) {
      jsonData.answer_key.forEach((answer) => {
        const questionNumber = answer?.question_number ?? answer?.number ?? answer?.qno;
        const answerText = answer?.answer_text ?? answer?.answer ?? answer?.correct_answer ?? '';
        if (questionNumber !== undefined && questionNumber !== null && answerText) {
          answerMap[String(questionNumber)] = answerText;
        }
      });
    }

    if (Array.isArray(jsonData?.questions)) {
      jsonData.questions.forEach((section) => {
        const questionsList = section?.questions_list || section?.questions || [];
        questionsList.forEach((question) => {
          const questionNumber = question?.question_number ?? question?.number ?? question?.qno;
          const answerText = question?.correct_answer ?? question?.answer_text ?? question?.answer ?? '';
          const questionKey = String(questionNumber);
          if (
            questionNumber !== undefined &&
            questionNumber !== null &&
            answerText &&
            !answerMap[questionKey]
          ) {
            answerMap[questionKey] = answerText;
          }
        });
      });
    }

    return answerMap;
  }, [activeWorksheet?.json_data]);

  const displayAnswers = useMemo(() => {
    if (showAnswerKey && Object.keys(derivedAnswerKey).length > 0) {
      return derivedAnswerKey;
    }
    return initialAnswers || {};
  }, [derivedAnswerKey, initialAnswers, showAnswerKey]);

  useEffect(() => {
    if (!activeWorksheet?.id) return;
    const currentKey = `${activeWorksheet.id}-${JSON.stringify(displayAnswers || {})}`;
    if (hydratedAnswersRef.current === currentKey) return;
    setUserAnswers(displayAnswers || {});
    hydratedAnswersRef.current = currentKey;
  }, [activeWorksheet?.id, displayAnswers]);

  const isMobileDevice = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const userAgent = window.navigator?.userAgent || '';
    const mobileUa = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|Mobile/i.test(userAgent);
    return mobileUa || window.innerWidth <= 768;
  }, []);

  useEffect(() => {
    // Check if KaTeX is available and load if needed
    if (window.katex) {
      setIsKaTeXLoaded(true);
      return;
    }
    
    // Load KaTeX scripts dynamically
    const loadKaTeX = async () => {
      try {
        // Load KaTeX CSS if not already loaded
        if (!document.querySelector('link[href*="katex"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.22/dist/katex.min.css';
          document.head.appendChild(link);
        }
        
        // Load KaTeX JS
        if (!window.katex) {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.22/dist/katex.min.js';
          script.onload = () => {
            // Load auto-render extension
            const autoRenderScript = document.createElement('script');
            autoRenderScript.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.22/dist/contrib/auto-render.min.js';
            autoRenderScript.onload = () => {
              setIsKaTeXLoaded(true);
            };
            document.head.appendChild(autoRenderScript);
          };
          document.head.appendChild(script);
        }
      } catch (error) {
        console.error('Error loading KaTeX:', error);
        setIsKaTeXLoaded(true); // Continue without math rendering
      }
    };
    
    loadKaTeX();
  }, []);
  
  // Load TikZJax for diagram rendering
  useEffect(() => {
    if (isMobileDevice) {
      setIsTikZJaxLoaded(true);
      return;
    }
    // Check if TikZJax is already loaded
    if (document.querySelector('script[src*="tikzjax"]')) {
      setIsTikZJaxLoaded(true);
      return;
    }
    
    const loadTikZJax = () => {
      try {
        // Load TikZJax CSS
        if (!document.querySelector('link[href*="tikzjax"]')) {
          const cssLink = document.createElement('link');
          cssLink.rel = 'stylesheet';
          cssLink.type = 'text/css';
          cssLink.href = 'https://tikzjax.com/v1/fonts.css';
          document.head.appendChild(cssLink);
        }
        
        // Load TikZJax JS
        const script = document.createElement('script');
        script.src = 'https://tikzjax.com/v1/tikzjax.js';
        script.onload = () => {
          console.log('TikZJax loaded successfully');
          setIsTikZJaxLoaded(true);
        };
        script.onerror = () => {
          console.error('Failed to load TikZJax');
          setIsTikZJaxLoaded(true); // Continue without TikZ rendering
        };
        document.head.appendChild(script);
      } catch (error) {
        console.error('Error loading TikZJax:', error);
        setIsTikZJaxLoaded(true);
      }
    };
    
    loadTikZJax();
  }, [isMobileDevice]);
  
  // Use refs to track if KaTeX has been rendered
  const katexRenderedRef = React.useRef(null);
  const answerKeyRenderedRef = React.useRef(false);
  const tikzRenderedRef = React.useRef(null);
  
  // Process TikZ diagrams after TikZJax loads and content is available
  useEffect(() => {
    if (isMobileDevice) return;
    if (isTikZJaxLoaded && activeWorksheet?.id && worksheetRenderKey !== tikzRenderedRef.current) {
      // Wait a bit for the HTML content to be fully rendered in DOM
      setTimeout(() => {
        try {
          // Find all TikZ script tags in the worksheet
          const tikzScripts = document.querySelectorAll('.enhanced-worksheet script[type="text/tikz"]');
          console.log(`Found ${tikzScripts.length} TikZ diagrams to render`);
          
          if (tikzScripts.length > 0 && window.tikzjax) {
            // Process each TikZ script
            tikzScripts.forEach((script, index) => {
              try {
                console.log(`Processing TikZ diagram ${index + 1}`);
                // Trigger TikZJax to process this script
                window.tikzjax.process(script);
              } catch (error) {
                console.error(`Error processing TikZ diagram ${index + 1}:`, error);
              }
            });
          } else if (tikzScripts.length > 0) {
            // Some environments attach tikzjax asynchronously; retry briefly without dispatching global lifecycle events.
            let attempts = 0;
            const intervalId = setInterval(() => {
              attempts += 1;
              if (window.tikzjax) {
                tikzScripts.forEach((script) => window.tikzjax.process(script));
                clearInterval(intervalId);
              } else if (attempts >= 10) {
                clearInterval(intervalId);
              }
            }, 200);
          }
          
          tikzRenderedRef.current = worksheetRenderKey;
        } catch (error) {
          console.error('Error processing TikZ diagrams:', error);
        }
      }, 500);
    }
  }, [isTikZJaxLoaded, activeWorksheet?.id, worksheetRenderKey]);
  
  // Render KaTeX for worksheet content once
  useEffect(() => {
    // Only render KaTeX once per worksheet to avoid disrupting user input
    if (isKaTeXLoaded && window.renderMathInElement && activeWorksheet?.id !== katexRenderedRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Render math in worksheet content - including question text, options, and labels
        const worksheetElements = document.querySelectorAll('.enhanced-worksheet .q-text, .enhanced-worksheet .q-head, .enhanced-worksheet label span');
        worksheetElements.forEach(element => {
          try {
            // Mark as processed to prevent re-rendering
            if (!element.classList.contains('katex-processed')) {
              window.renderMathInElement(element, {
                delimiters: [
                  { left: '$$', right: '$$', display: true },
                  { left: '$', right: '$', display: false },
                  { left: '\\[', right: '\\]', display: true },
                  { left: '\\(', right: '\\)', display: false },
                ],
                throwOnError: false,
                errorColor: '#cc0000',
                strict: false,
                // Prevent KaTeX from processing input elements and their containers
                ignoredTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code', 'input', 'button'],
                ignoredClasses: ['answer-input', 'mcq-input', 'tf-input', 'ar-input', 'fib-input', 'sa-input', 'la-input']
              });
              element.classList.add('katex-processed');
            }
          } catch (error) {
            console.error('Error rendering math:', error);
          }
        });
        // Mark this worksheet as rendered
        katexRenderedRef.current = activeWorksheet?.id;
      }, 100);
    }
  }, [isKaTeXLoaded, activeWorksheet?.id]);
  
  // Render KaTeX for answer key when it becomes visible
  useEffect(() => {
    if (isKaTeXLoaded && window.renderMathInElement && showAnswerKey && !answerKeyRenderedRef.current) {
      setTimeout(() => {
        const answerKeyElements = document.querySelectorAll('.answer-key-content');
        answerKeyElements.forEach(element => {
          try {
            window.renderMathInElement(element, {
              delimiters: [
                { left: '$$', right: '$$', display: true },
                { left: '$', right: '$', display: false },
                { left: '\\[', right: '\\]', display: true },
                { left: '\\(', right: '\\)', display: false },
              ],
              throwOnError: false,
              errorColor: '#cc0000',
              strict: false
            });
          } catch (error) {
            console.error('Error rendering math in answer key:', error);
          }
        });
        answerKeyRenderedRef.current = true;
      }, 100);
    } else if (!showAnswerKey) {
      // Reset flag when answer key is hidden
      answerKeyRenderedRef.current = false;
    }
  }, [isKaTeXLoaded, showAnswerKey]);

  const mapSectionType = (sectionType = '') => {
    if (sectionType.includes('Multiple Choice')) return 'Multiple Choice';
    if (sectionType.includes('Fill in the Blank')) return 'Fill in the Blanks';
    if (sectionType.includes('True') || sectionType.includes('False')) return 'True False';
    if (sectionType.includes('Assertion')) return 'Assertion Reasoning';
    if (sectionType.includes('Short Answer')) return 'Short Answer';
    if (sectionType.includes('Long Answer')) return 'Long Answer';
    return 'Multiple Choice';
  };

  const buildQuestionsFromJson = (worksheetJson) => {
    const allQuestions = [];
    const answerMap = {};
    if (Array.isArray(worksheetJson?.answer_key)) {
      worksheetJson.answer_key.forEach((answer) => {
        const questionNumber = answer?.question_number ?? answer?.number ?? answer?.qno;
        const answerText = answer?.answer_text ?? answer?.answer ?? answer?.correct_answer ?? '';
        if (questionNumber !== undefined && questionNumber !== null) {
          answerMap[String(questionNumber)] = answerText;
        }
      });
    }

    if (Array.isArray(worksheetJson?.questions)) {
      worksheetJson.questions.forEach((section) => {
        const questionsList = section?.questions_list || section?.questions || [];
        const sectionType = section?.question_type || 'Multiple Choice';
        const mappedType = mapSectionType(sectionType);
        questionsList.forEach((q) => {
          const questionNumber = q.question_number || q.number || q.qno || allQuestions.length + 1;
          let questionOptions = q.options || [];
          if (mappedType === 'True False') {
            questionOptions = ['True', 'False'];
          } else if (
            mappedType === 'Short Answer' ||
            mappedType === 'Long Answer' ||
            mappedType === 'Fill in the Blanks' ||
            mappedType === 'Assertion Reasoning'
          ) {
            questionOptions = [];
          }
          const normalized = {
            question_number: questionNumber,
            question_type: mappedType,
            question_text: q.question_text || q.question || q.text || '',
            options: questionOptions,
            correct_answer: answerMap[String(questionNumber)] || '',
            marks: parseInt(q.marks || 1, 10),
            concept: q.concept || '',
            skill: q.skill || '',
            difficulty: q.difficulty_level || q.difficulty || 'medium',
            topic: q.topic || ''
          };
          if (q.diagram) {
            normalized.diagram = q.diagram;
          }
          allQuestions.push(normalized);
        });
      });
    }

    return allQuestions;
  };

  const convertTikzToSvg = async (tikzCode) => {
    const cleanedTikzCode = sanitizeTikzCode(tikzCode);
    if (!cleanedTikzCode || !document?.body) return null;

    return new Promise((resolve) => {
      const conversionFrame = document.createElement('iframe');
      conversionFrame.style.position = 'absolute';
      conversionFrame.style.left = '-9999px';
      conversionFrame.style.width = '1px';
      conversionFrame.style.height = '1px';
      conversionFrame.style.border = '0';
      conversionFrame.setAttribute('aria-hidden', 'true');
      conversionFrame.sandbox = 'allow-scripts';

      let cleanedUp = false;
      const requestId = `tikz-convert-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        window.removeEventListener('message', onMessage);
        conversionFrame.remove();
      };

      const finish = (value) => {
        cleanup();
        resolve(value);
      };

      const timeoutId = window.setTimeout(() => {
        finish(null);
      }, 12000);

      const onMessage = (event) => {
        if (event?.data?.type !== 'TIKZ_TO_SVG_RESULT' || event?.data?.requestId !== requestId) {
          return;
        }

        window.clearTimeout(timeoutId);
        finish(event?.data?.svg || null);
      };

      window.addEventListener('message', onMessage);
      document.body.appendChild(conversionFrame);

      const encodedTikz = encodeURIComponent(cleanedTikzCode);

      const srcDoc = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <link rel="stylesheet" type="text/css" href="https://tikzjax.com/v1/fonts.css" />
  </head>
  <body>
    <div id="tikz-root"></div>
    <script>
      window.__TIKZ_CODE__ = decodeURIComponent(${JSON.stringify(encodedTikz)});
      window.__REQUEST_ID__ = ${JSON.stringify(requestId)};
    </script>
    <script src="https://tikzjax.com/v1/tikzjax.js"></script>
    <script>
      (function runConversion() {
        const parentWindow = window.parent;
        const requestId = window.__REQUEST_ID__;
        const root = document.getElementById('tikz-root');

        const postResult = (svg) => {
          parentWindow.postMessage({ type: 'TIKZ_TO_SVG_RESULT', requestId, svg: svg || null }, '*');
        };

        try {
          const tikzScript = document.createElement('script');
          tikzScript.type = 'text/tikz';
          tikzScript.textContent = window.__TIKZ_CODE__ || '';
          root.appendChild(tikzScript);

          const tryProcess = () => {
            if (!window.tikzjax || typeof window.tikzjax.process !== 'function') {
              return false;
            }
            window.tikzjax.process(tikzScript);
            return true;
          };

          const start = Date.now();
          const processPoller = window.setInterval(() => {
            const processed = tryProcess();
            if (processed || Date.now() - start > 6000) {
              window.clearInterval(processPoller);
            }
          }, 50);

          const findSvg = () => root.querySelector('svg') || document.querySelector('svg');
          const renderStart = Date.now();
          const renderPoller = window.setInterval(() => {
            const svg = findSvg();
            if (svg) {
              window.clearInterval(renderPoller);
              postResult(svg.outerHTML);
              return;
            }

            if (Date.now() - renderStart > 10000) {
              window.clearInterval(renderPoller);
              postResult(null);
            }
          }, 75);
        } catch (error) {
          postResult(null);
        }
      })();
    </script>
  </body>
</html>`;

      conversionFrame.srcdoc = srcDoc;
    });
  };

  const normalizeSvgForDisplay = (svgMarkup) => {
    if (!svgMarkup || typeof svgMarkup !== 'string') return '';

    try {
      const parser = new DOMParser();
      const parsed = parser.parseFromString(svgMarkup, 'image/svg+xml');
      const svg = parsed.querySelector('svg');
      if (!svg) return svgMarkup;

      const existingStyle = svg.getAttribute('style') || '';
      const normalizedStyle = [
        existingStyle.trim(),
        'display:block',
        'margin:0 auto',
        'max-width:100%',
        'height:auto',
        'position:static'
      ].filter(Boolean).join('; ');

      svg.setAttribute('style', normalizedStyle);
      return svg.outerHTML;
    } catch (error) {
      console.error('Unable to normalize SVG markup:', error);
      return svgMarkup;
    }
  };

  const normalizeQuestionId = (value) => {
    if (value === null || value === undefined) return '';
    const strValue = String(value).trim();
    if (!strValue) return '';
    const numericMatch = strValue.match(/\d+/);
    return numericMatch ? numericMatch[0] : strValue;
  };

  const closeDiagramEditor = () => {
    setDiagramEditor({
      isOpen: false,
      questionId: null,
      tikzCode: '',
      originalTikzCode: '',
      svgCode: '',
      originalSvgCode: '',
      editorMode: 'tikz',
      isIframe: false,
      doc: null,
      excalidrawOpen: false,
      excalidrawElements: null
    });
    setDiagramEditorError('');
  };

  // Prefer server-side conversion (fast when healthy) then fall back to the
  // in-browser TikZJax renderer that powers DiagramTile. The local
  // `convertTikzToSvg` above relies on `window.tikzjax.process()`, which
  // TikZJax v1 does not reliably expose for ad-hoc calls — without a
  // working fallback "Regenerate SVG" silently failed whenever the
  // sidecar was unreachable.
  const tikzToSvgPreferServer = (tikzCode) => renderTikzToSvgWithFallback(tikzCode);

  const handleRegenerateSvg = async () => {
    if (!diagramEditor.tikzCode) {
      setDiagramEditorError('Enter TikZ code to regenerate SVG.');
      return;
    }
    setDiagramEditorError('');
    const updatedSvg = await tikzToSvgPreferServer(diagramEditor.tikzCode);
    if (!updatedSvg) {
      setDiagramEditorError('TikZ-to-SVG conversion failed. Please review the TikZ code.');
      return;
    }
    const normalized = normalizeSvgForDisplay(updatedSvg);
    // Refresh the shared TikZ→SVG cache so the worksheet HTML re-renderer
    // and any other live tiles for the same source pick up the fresh SVG
    // through subscribeCacheUpdate.
    cacheSvg(diagramEditor.tikzCode, normalized);
    setDiagramEditor((prev) => ({ ...prev, svgCode: normalized }));
  };

  const svgPreviewDoc = useMemo(() => {
    if (!diagramEditor.svgCode) return '';

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      html, body { margin: 0; padding: 0; background: #f9fafb; }
      body { display: flex; justify-content: center; align-items: flex-start; min-height: 100vh; padding: 12px; box-sizing: border-box; overflow: auto; }
      svg { max-width: 100%; height: auto; display: block; margin: 0 auto; }
    </style>
  </head>
  <body>
    ${diagramEditor.svgCode}
  </body>
</html>`;
  }, [diagramEditor.svgCode]);

  const handleSaveDiagram = async () => {
    if (!canEditDiagram) {
      setDiagramEditorError('Only the task owner can edit diagrams.');
      return;
    }
    if (!diagramEditor.questionId || !activeWorksheet?.id) {
      setDiagramEditorError('Diagram context is missing. Please reopen editor and try again.');
      return;
    }

    let svgCodeToSave = diagramEditor.svgCode;
    const tikzWasEdited =
      diagramEditor.editorMode === 'tikz'
      && diagramEditor.tikzCode
      && diagramEditor.tikzCode !== diagramEditor.originalTikzCode;

    // svgCode was pre-loaded from the existing svg_cache when the editor
    // opened, so it's stale the moment the user edits the TikZ. Force a
    // fresh render whenever the TikZ has changed, otherwise the worksheet
    // would persist the new TikZ paired with the old rendered SVG and the
    // display would show the wrong picture.
    if (diagramEditor.tikzCode && (tikzWasEdited || !svgCodeToSave)) {
      const renderedSvg = await tikzToSvgPreferServer(diagramEditor.tikzCode);
      if (!renderedSvg) {
        setDiagramEditorError('Unable to save: TikZ could not be converted to SVG. Please fix TikZ and regenerate preview.');
        return;
      }
      svgCodeToSave = normalizeSvgForDisplay(renderedSvg);
      setDiagramEditor((prev) => ({ ...prev, svgCode: svgCodeToSave }));
    }

    if (!svgCodeToSave) {
      setDiagramEditorError('SVG output is required before saving.');
      return;
    }

    let worksheetJson;
    try {
      worksheetJson = typeof activeWorksheet.json_data === 'string'
        ? JSON.parse(activeWorksheet.json_data)
        : activeWorksheet.json_data;
    } catch (error) {
      console.error('Failed to parse worksheet JSON:', error);
      setDiagramEditorError('Unable to parse worksheet JSON for update.');
      return;
    }

    const questions = buildQuestionsFromJson(worksheetJson);
    const normalizedTargetQuestionId = normalizeQuestionId(diagramEditor.questionId);
    const targetIndex = questions.findIndex(
      (q) => normalizeQuestionId(q.question_number) === normalizedTargetQuestionId
    );

    if (targetIndex === -1) {
      setDiagramEditorError('Unable to locate the diagram question in worksheet data.');
      return;
    }
    const hasTikzChanged = diagramEditor.tikzCode !== diagramEditor.originalTikzCode;
    const shouldSaveTikz = hasTikzChanged && diagramEditor.editorMode === 'tikz';

    const svgDiagram = { type: 'svg', code: normalizeSvgForDisplay(svgCodeToSave) };
    if (!shouldSaveTikz && diagramEditor.excalidrawElements?.length) {
      svgDiagram.excalidraw_elements = diagramEditor.excalidrawElements;
    }
    const updatedDiagram = shouldSaveTikz
      ? { type: 'tikz', code: diagramEditor.tikzCode, svg_cache: normalizeSvgForDisplay(svgCodeToSave) }
      : svgDiagram;

    questions[targetIndex] = {
      ...questions[targetIndex],
      diagram: updatedDiagram
    };

    setIsSavingDiagram(true);
    setDiagramEditorError('');

    try {
      await axios.put(`/worksheets/${activeWorksheet.id}`, {
        questions,
        num_questions: questions.length
      });

      // Prime the local TikZ→SVG cache with the freshly rendered SVG so
      // every surface that reads getCachedSvg() (worksheet HTML re-renderer,
      // OneQuestionAtATime's DiagramRenderer, QuestionBrowser tiles) flips
      // to the new picture immediately. The notifyCacheUpdate inside
      // cacheSvg wakes the subscribers without us touching their state.
      if (shouldSaveTikz && diagramEditor.tikzCode && svgCodeToSave) {
        cacheSvg(diagramEditor.tikzCode, svgCodeToSave);
      }

      const refreshed = await axios.get(`/worksheets/${activeWorksheet.id}?t=${Date.now()}`);
      setLocalWorksheet(refreshed.data);
      if (onWorksheetUpdated) {
        onWorksheetUpdated(refreshed.data);
      }
      closeDiagramEditor();
    } catch (error) {
      console.error('Error saving diagram:', error);
      setDiagramEditorError(error.response?.data?.detail || 'Failed to save diagram updates.');
    } finally {
      setIsSavingDiagram(false);
    }
  };

  // Handle answer changes for online mode with functional updates
  const handleAnswerChange = React.useCallback((questionId, answer) => {
    console.log('Answer changed:', { questionId, answer });
    setUserAnswers(prevAnswers => {
      const newAnswers = { ...prevAnswers, [questionId]: answer };
      console.log('New answers state:', newAnswers);
      // Notify parent component
      if (onAnswersChange) {
        onAnswersChange(newAnswers);
      }
      return newAnswers;
    });
  }, [onAnswersChange]);

  // Set up answer change listeners for online mode
  useEffect(() => {
    if (mode !== 'online') return;
    
    let timeoutId;
    const listeners = new Map(); // Track listeners for cleanup
    
    const setupAnswerListeners = () => {
      console.log('Setting up answer listeners...');
      
      // Determine which document to search (iframe or main document)
      let searchDocument = document;
      if (hasTikZ && iframeRef.current) {
        searchDocument = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
        console.log('Using iframe document for answer listeners');
      } else {
        console.log('Using main document for answer listeners');
      }
      
      // Find all radio/checkbox inputs
      const radioInputs = searchDocument.querySelectorAll('.enhanced-worksheet input[type="radio"], .enhanced-worksheet input[type="checkbox"], input[type="radio"], input[type="checkbox"]');
      console.log('Found radio/checkbox inputs:', radioInputs.length);
      
      radioInputs.forEach(input => {
        const changeHandler = (e) => {
          console.log('Radio/checkbox changed:', e.target.name, e.target.value);
          const questionId = e.target.name.replace('question_', '');
          handleAnswerChange(questionId, e.target.value);
          
          // Ensure visual feedback - the radio button stays checked
          e.target.checked = true;
          
          // Uncheck other radios in same group
          const radioGroup = searchDocument.querySelectorAll(`input[name="${e.target.name}"]`);
          radioGroup.forEach(radio => {
            if (radio !== e.target) {
              radio.checked = false;
            }
          });
        };
        
        input.addEventListener('change', changeHandler);
        listeners.set(input, { event: 'change', handler: changeHandler });
      });

      // Find all text inputs
      const textInputs = searchDocument.querySelectorAll('.enhanced-worksheet input[type="text"], .enhanced-worksheet textarea, input[type="text"], textarea');
      console.log('Found text inputs:', textInputs.length);
      
      textInputs.forEach(input => {
        const inputHandler = (e) => {
          console.log('Text input changed:', e.target.name, 'Value length:', e.target.value?.length);
          
          // Extract question ID from name (handle both simple and FIB formats)
          // Format: question_1 or question_1_blank_1
          const nameParts = e.target.name.replace('question_', '').split('_');
          const questionId = nameParts[0];
          
          // For Fill in the Blanks with multiple inputs, collect all blank answers
          if (e.target.name.includes('_blank_')) {
            // Get all inputs for this question
            const allBlanks = searchDocument.querySelectorAll(`[name^="question_${questionId}_blank_"]`);
            const blankAnswers = [];
            allBlanks.forEach(blank => {
              if (blank.value) {
                blankAnswers.push(blank.value);
              }
            });
            // Combine all blank answers with commas
            const combinedAnswer = blankAnswers.join(', ');
            handleAnswerChange(questionId, combinedAnswer);
            console.log(`FIB Question ${questionId}: ${blankAnswers.length} blanks, combined: ${combinedAnswer}`);
          } else {
            // Regular text input or textarea
            handleAnswerChange(questionId, e.target.value);
            console.log(`Question ${questionId}: ${e.target.value?.substring(0, 50)}...`);
          }
        };
        
        input.addEventListener('input', inputHandler);
        listeners.set(input, { event: 'input', handler: inputHandler });
      });
      
      console.log('Total listeners attached:', listeners.size);

      // Attach an optional math-keyboard toggle to each free-text answer
      // field. Skipped for TikZ worksheets: they render inside an iframe
      // with its own isolated customElements registry, and <math-field>
      // registered in the main window wouldn't be recognized there.
      if (searchDocument === document && worksheetContentRef.current) {
        setupMathToggles(worksheetContentRef.current, listeners);
      }
    };

    // Setup listeners after DOM is ready and KaTeX is done
    // For iframe content, wait longer to ensure iframe is fully loaded
    const delay = hasTikZ ? 3000 : 500;
    timeoutId = setTimeout(setupAnswerListeners, delay);
    
    // Cleanup function
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      
      // Remove all listeners
      listeners.forEach((listenerInfo, element) => {
        element.removeEventListener(listenerInfo.event, listenerInfo.handler);
      });
      listeners.clear();
      console.log('Cleaned up answer listeners');
    };
  }, [mode, activeWorksheet?.id, handleAnswerChange, hasTikZ]);

  useEffect(() => {
    if (!activeWorksheet?.id) return;
    if (!isContentReady || hasTikZ === null) return;

    const applyInitialAnswers = () => {
      const searchDocument = hasTikZ && iframeRef.current
        ? iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document
        : document;

      if (!searchDocument) return;

      const normalizeForCompare = (value) => {
        if (value === null || value === undefined) return '';
        return String(value)
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .replace(/[^a-z0-9]/gi, '');
      };

      const extractOptionKey = (value) => {
        if (value === null || value === undefined) return null;
        const trimmed = String(value).trim();

        // Standalone markers: "A", "(A)", "A)", "1", "(iv)", etc.
        const standaloneMatch = trimmed.match(/^\(?\s*([A-Da-d]|[1-9]|[IVXLCivxlc]+)\s*\)?[).:\-]?\s*$/);
        if (standaloneMatch) {
          return standaloneMatch[1].toUpperCase();
        }

        // Prefixed option formats only: "A) ...", "A. ...", "1) ...", "IV: ..."
        const prefixedMatch = trimmed.match(/^\(?\s*([A-Da-d]|[1-9]|[IVXLCivxlc]+)\s*\)?[).:\-]\s+/);
        return prefixedMatch ? prefixedMatch[1].toUpperCase() : null;
      };

      const normalizeAssertionReasoningOption = (value) => {
        if (value === null || value === undefined) return null;

        const explicitKey = extractOptionKey(value);
        if (explicitKey && ['A', 'B', 'C', 'D', 'E'].includes(explicitKey)) {
          return explicitKey;
        }

        const normalized = normalizeForCompare(value);
        const assertionOptionMap = {
          bothaandraretrueandristhecorrectexplanationofa: 'A',
          bothaandraretrueandriscorrectexplanation: 'A',
          bothaandraretruebutrisnotthecorrectexplanationofa: 'B',
          bothaandraretruebutrisnotcorrectexplanation: 'B',
          aistruebutrisfalse: 'C',
          aisfalsebutristrue: 'D',
          aisfalseandrisfalse: 'E',
        };

        return assertionOptionMap[normalized] || null;
      };

      const resetInputs = () => {
        const inputs = searchDocument.querySelectorAll(
          'input[name^="question_"], textarea[name^="question_"]'
        );

        inputs.forEach(input => {
          if (input.type === 'radio' || input.type === 'checkbox') {
            input.checked = false;
          } else {
            input.value = '';
          }
        });
      };

      if (!displayAnswers || Object.keys(displayAnswers).length === 0) {
        if (!showAnswerKey) {
          resetInputs();
        }
        return;
      }

      if (!showAnswerKey) {
        resetInputs();
      }

      Object.entries(displayAnswers).forEach(([questionId, answerValue]) => {
        if (answerValue === null || answerValue === undefined) return;

        const stringAnswer = Array.isArray(answerValue)
          ? answerValue.map(value => String(value))
          : String(answerValue);

        const blankInputs = searchDocument.querySelectorAll(
          `[name^="question_${questionId}_blank_"]`
        );

        if (blankInputs.length > 0) {
          const blankValues = Array.isArray(stringAnswer)
            ? stringAnswer
            : stringAnswer
                .split(',')
                .map(value => value.trim())
                .filter(Boolean);

          blankInputs.forEach((input, index) => {
            if (blankValues[index] !== undefined) {
              input.value = blankValues[index];
            }
          });
          return;
        }

        const optionInputs = searchDocument.querySelectorAll(
          `input[name="question_${questionId}"][type="radio"], input[name="question_${questionId}"][type="checkbox"]`
        );

        if (optionInputs.length > 0) {
          const selectedValues = Array.isArray(stringAnswer)
            ? stringAnswer.map(value => String(value).trim())
            : String(stringAnswer)
                .split(',')
                .map(value => value.trim())
                .filter(Boolean);
          optionInputs.forEach(input => {
            const inputValue = String(input.value ?? '');
            const labelText = input.closest('label')?.textContent?.replace(/\s+/g, ' ').trim() || '';
            const optionKey = extractOptionKey(labelText) || extractOptionKey(inputValue);
            const optionAssertionKey = normalizeAssertionReasoningOption(labelText) || normalizeAssertionReasoningOption(inputValue);
            const inputNormalized = normalizeForCompare(inputValue);
            const labelNormalized = normalizeForCompare(labelText);

            const isMatch = selectedValues.some(selectedValue => {
              const normalizedValue = normalizeForCompare(selectedValue);
              if (!normalizedValue) return false;
              if (inputNormalized === normalizedValue) return true;
              if (labelNormalized === normalizedValue) return true;
              const answerKey = extractOptionKey(selectedValue);
              if (answerKey && optionKey && answerKey === optionKey) return true;

              const selectedAssertionKey = normalizeAssertionReasoningOption(selectedValue);
              if (selectedAssertionKey && optionAssertionKey && selectedAssertionKey === optionAssertionKey) return true;

              if (normalizedValue.length >= 4) {
                if (labelNormalized && labelNormalized.includes(normalizedValue)) return true;
                if (labelNormalized && normalizedValue.includes(labelNormalized)) return true;
              }
              return false;
            });

            input.checked = isMatch;
          });
          return;
        }

        const textInput = searchDocument.querySelector(
          `input[name="question_${questionId}"], textarea[name="question_${questionId}"]`
        );
        if (textInput) {
          textInput.value = String(stringAnswer);
        }
      });
    };

    const timeoutId = setTimeout(applyInitialAnswers, hasTikZ ? 300 : 0);
    return () => clearTimeout(timeoutId);
  }, [hasTikZ, displayAnswers, isContentReady, showAnswerKey, activeWorksheet?.id]);
  
  // Use ref to set HTML only once and prevent DOM resets
  const worksheetContentRef = React.useRef(null);
  const htmlSetRef = React.useRef(null);
  
  // Check if worksheet content has TikZ diagrams
  useEffect(() => {
    if (sanitizedWorksheetHtmlContent) {
      const containsTikZ = !tikzCachedWorksheetContent.allCached && (
        sanitizedWorksheetHtmlContent.includes('type="text/tikz"') || sanitizedWorksheetHtmlContent.includes("type='text/tikz'")
      );
      setHasTikZ(containsTikZ);
      console.log('EnhancedWorksheetDisplay: Content has TikZ diagrams:', containsTikZ);
    } else {
      setHasTikZ(false);
    }
  }, [sanitizedWorksheetHtmlContent, tikzCachedWorksheetContent.allCached, activeWorksheet?.id]);
  
  // The iframe path used to host TikZJax for in-place rendering, but the
  // worksheet view now never runs TikZJax — uncached TikZ scripts have been
  // swapped for "View Diagram" buttons that open the dedicated viewer modal.
  // Force the in-page render path everywhere so there is no stale iframe
  // document holding a defunct TikZJax instance.
  const shouldRenderTikzInIframe = false;

  const getMobileSafeHtmlContent = (htmlContent = '') => {
    const strippedTikzScripts = htmlContent.replace(
      /<script[^>]*type=(?:"text\/tikz"|'text\/tikz')[^>]*>[\s\S]*?<\/script>/gi,
      '<div class="diagram-placeholder">Diagram hidden on mobile for stability. Open this worksheet on desktop/tablet to view diagram.</div>',
    );
    return strippedTikzScripts;
  };

  // For content with TikZ, render in iframe
  useEffect(() => {
    const attachIframeAutoResize = () => {
      if (!iframeRef.current) return;

      const iframeElement = iframeRef.current;
      const iframeDoc = iframeElement.contentDocument;
      const iframeWindow = iframeElement.contentWindow;
      const iframeBody = iframeDoc?.body;

      if (!iframeDoc || !iframeWindow || !iframeBody) return;

      let animationFrameId = null;
      const adjustHeight = () => {
        if (!iframeRef.current) return;
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
        animationFrameId = requestAnimationFrame(() => {
          const currentIframe = iframeRef.current;
          if (!currentIframe || currentIframe !== iframeElement || !currentIframe.isConnected) return;
          const nextHeight = Math.max(
            iframeBody.scrollHeight,
            iframeDoc.documentElement?.scrollHeight || 0
          );
          currentIframe.style.height = `${nextHeight + 4}px`;
        });
      };

      const mutationObserver = new MutationObserver(adjustHeight);
      mutationObserver.observe(iframeBody, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true
      });

      iframeWindow.addEventListener('load', adjustHeight);
      iframeWindow.addEventListener('resize', adjustHeight);
      adjustHeight();

      iframeResizeCleanupRef.current = () => {
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
        mutationObserver.disconnect();
        iframeWindow.removeEventListener('load', adjustHeight);
        iframeWindow.removeEventListener('resize', adjustHeight);
      };
    };

    const currentKey = worksheetRenderKey;
    if (shouldRenderTikzInIframe && iframeRef.current && sanitizedWorksheetHtmlContent && htmlSetRef.current !== currentKey) {
      if (iframeResizeCleanupRef.current) {
        iframeResizeCleanupRef.current();
        iframeResizeCleanupRef.current = null;
      }
      // Navigate to about:blank so the browser immediately discards the
      // TikZJax WebAssembly context rather than waiting for GC.
      if (iframeRef.current) {
        try { iframeRef.current.src = 'about:blank'; } catch (_) {}
      }

      setIsContentReady(false); // Mark as not ready while loading
      const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
      
      const fullHTML = `
        <!DOCTYPE html>
        <html>
        <html${isDarkMode ? ' class="dark"' : ''}>
        <head>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.22/dist/katex.min.css">
          <link rel="stylesheet" type="text/css" href="https://tikzjax.com/v1/fonts.css">
          <style>
            body { 
              margin: 0; 
              padding: 10px; 
              font-family: Arial, sans-serif;
              line-height: 1.6;
              color: #0f172a;
              background-color: transparent;
            }
            .worksheet-content { 
              font-family: Arial, sans-serif; 
              line-height: 1.6; 
            }
            .worksheet-content .marks { 
              color: #666; 
              font-weight: bold; 
              background: #f0f0f0;
              padding: 2px 6px;
              border-radius: 3px;
              font-size: 0.9em;
            }
            /* Answer input styles */
            .answer-input { 
              margin-top: 0.75rem; 
              padding: 0.5rem; 
              background: #f9f9f9; 
              border-radius: 0.25rem; 
            }
            .answer-input label {
              display: flex;
              align-items: center;
              padding: 8px;
              margin: 4px 0;
              border-radius: 4px;
              cursor: pointer;
              transition: background-color 0.2s;
            }
            .answer-input label:hover {
              background-color: #f3f4f6;
            }
            .answer-input input[type="radio"], .answer-input input[type="checkbox"] {
              margin-right: 8px;
              width: 16px;
              height: 16px;
              cursor: pointer;
            }
            .answer-input input[type="text"], .answer-input textarea { 
              width: 100% !important;
              padding: 0.5rem;
              border: 1px solid #d1d5db;
              border-radius: 0.25rem;
              font-family: inherit;
              font-size: 1rem;
              margin-bottom: 0.25rem;
            }
            .answer-input input[type="text"] {
              min-height: 2.5rem;
            }
            .answer-input textarea {
              min-height: 6rem;
              resize: vertical;
            }
            .answer-input input[type="radio"]:checked + span,
            .answer-input input[type="radio"]:checked ~ span {
              font-weight: 600;
              color: #2563eb;
            }
            .answer-input label:has(input[type="radio"]:checked) {
              background-color: #dbeafe;
              border: 1px solid #3b82f6;
            }

            /* ---- Dark mode mirror of the styles above ---- */
            html.dark body { color: #e2e8f0; }
            html.dark .worksheet-content { color: #e2e8f0; }
            html.dark .worksheet-content .marks {
              color: #cbd5e1;
              background: #1f2937;
            }
            html.dark .worksheet-content table th,
            html.dark .worksheet-content table td {
              border-color: #475569;
              color: #e2e8f0;
            }
            html.dark .worksheet-content table th { background: #1f2937; }
            html.dark .worksheet-content hr { border-color: #334155; }
            html.dark .answer-input { background: rgba(15, 23, 42, 0.5); }
            html.dark .answer-input label:hover { background-color: rgba(148, 163, 184, 0.12); }
            html.dark .answer-input input[type="text"],
            html.dark .answer-input textarea {
              background-color: #1e293b;
              border-color: #475569;
              color: #f1f5f9;
            }
            html.dark .answer-input input[type="text"]::placeholder,
            html.dark .answer-input textarea::placeholder { color: #64748b; }
            html.dark .answer-input input[type="radio"]:checked + span,
            html.dark .answer-input input[type="radio"]:checked ~ span { color: #93c5fd; }
            html.dark .answer-input label:has(input[type="radio"]:checked) {
              background-color: rgba(59, 130, 246, 0.18);
              border: 1px solid #3b82f6;
            }
            /* TikZ output is monochrome SVG — keep it on a light card so
               diagrams stay legible against the dark page. */
            html.dark .diagram svg,
            html.dark .diagram img {
              background-color: #f8fafc;
              border-radius: 0.5rem;
            }
          </style>
        </head>
        <body${isDarkMode ? ' class="dark"' : ''}>
          ${labelDiagramsInHtml(sanitizedWorksheetHtmlContent)}
          <script src="https://tikzjax.com/v1/tikzjax.js"></script>
        </body>
        </html>
      `;
      
      iframeDoc.open();
      iframeDoc.write(fullHTML);
      iframeDoc.close();
      attachIframeAutoResize();
      
      htmlSetRef.current = currentKey;
      console.log('EnhancedWorksheetDisplay: Iframe content loaded with TikZJax');
      
      // Mark content as ready immediately to prevent disappearance
      setIsContentReady(true);      
      const captureTimer = window.setTimeout(() => {
        const serverEntries = [];
        try {
          const scripts = Array.from(iframeDoc.querySelectorAll('script[type="text/tikz"]'));
          const svgs = Array.from(iframeDoc.querySelectorAll('svg'));
          scripts.forEach((scriptNode, index) => {
            const tikzCode = scriptNode?.textContent || '';
            const svgNode = svgs[index];
            if (tikzCode && svgNode) {
              const svgHtml = svgNode.outerHTML;
              cacheSvg(tikzCode, svgHtml);
              serverEntries.push({ tikzCode, svgHtml });
            }
          });
        } catch (error) {
          console.warn('[EnhancedWorksheetDisplay] Cache hydration failed:', error);
        }
        // Persist browser-rendered SVGs back to the worksheet record so other
        // devices/users get them inline without re-running TikZJax. The server
        // is the source of truth for the hash match — anything we send that
        // doesn't correspond to a current diagram is silently dropped.
        if (activeWorksheet?.id && serverEntries.length > 0) {
          postSvgCacheToServer(activeWorksheet.id, serverEntries);
        }
      }, 3000);
      return () => window.clearTimeout(captureTimer);
    }

    return () => {
      if (iframeResizeCleanupRef.current) {
        iframeResizeCleanupRef.current();
        iframeResizeCleanupRef.current = null;
      }
    };
  }, [shouldRenderTikzInIframe, sanitizedWorksheetHtmlContent, worksheetRenderKey]);

  const canEditDiagramEffective = canEditDiagram && !isMobileDevice;
    
  // Event delegation for the embedded "View Diagram" buttons. innerHTML
  // can't bind React event listeners directly, so we attach a single
  // click handler on the worksheetContentRef wrapper and dispatch by
  // data-tikz-view-index.
  //
  // Clicking triggers a background render of the TikZ in an offscreen
  // iframe (same path the background prerender uses) rather than opening
  // a modal — the modal was destabilising on mobile WebViews. When the
  // SVG lands in cache, the worksheet HTML re-renders via tikzCacheTick
  // and the button is swapped for the inline SVG.
  const handleWorksheetClick = React.useCallback((event) => {
    const button = event.target.closest('.tikz-view-btn');
    if (!button) return;
    event.preventDefault();
    const idx = parseInt(button.getAttribute('data-tikz-view-index'), 10);
    if (Number.isNaN(idx) || idx < 0) return;
    const job = (tikzCachedWorksheetContent.viewerDiagrams || [])[idx];
    if (!job || !job.tikzCode) return;
    // Idempotent: already rendering for this index? do nothing.
    if (renderingIndices.has(idx)) return;
    // Fast-path: cache already has it (background prerender finished
    // between the HTML emit and this click). Bump the tick so the HTML
    // re-renders without spinning up another iframe.
    if (getCachedSvg(job.tikzCode)) {
      setTikzCacheTick((tick) => tick + 1);
      return;
    }
    setRenderingIndices((prev) => {
      if (prev.has(idx)) return prev;
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
    const worksheetIdAtClick = activeWorksheet?.id;
    renderTikzToSvg(job.tikzCode, { timeoutMs: 25000 })
      .then((svgHtml) => {
        if (!svgHtml) return;
        cacheSvg(job.tikzCode, svgHtml);
        if (worksheetIdAtClick) {
          postSvgCacheToServer(worksheetIdAtClick, [{ tikzCode: job.tikzCode, svgHtml }]);
        }
      })
      .catch(() => { /* failure leaves the button as-is; user can retry */ })
      .finally(() => {
        setRenderingIndices((prev) => {
          if (!prev.has(idx)) return prev;
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });
      });
  }, [tikzCachedWorksheetContent.viewerDiagrams, renderingIndices, activeWorksheet?.id]);

  // Background prerender for uncached TikZ diagrams: as soon as the
  // worksheet is shown we walk the uncached list and compile each TikZ to
  // SVG in an offscreen iframe (one at a time, yielding to the browser
  // between jobs). Each cached SVG then bubbles up via subscribeCacheUpdate
  // and the worksheet HTML swaps the "View Diagram" button for the inline
  // SVG without requiring user interaction.
  //
  // The job list is derived from json_data rather than the
  // tikzCachedWorksheetContent memo so this effect doesn't re-arm on every
  // cache tick — we want the queue to keep running through completion,
  // not restart after each render.
  useEffect(() => {
    if (!activeWorksheet?.id) return undefined;
    if (typeof window === 'undefined') return undefined;
    let json = activeWorksheet?.json_data;
    if (typeof json === 'string') {
      try { json = JSON.parse(json); } catch (_e) { return undefined; }
    }
    if (!json || !Array.isArray(json.questions)) return undefined;

    const jobs = [];
    json.questions.forEach((section) => {
      (section?.questions_list || []).forEach((q) => {
        const diagram = q?.diagram;
        if (!diagram || typeof diagram !== 'object') return;
        // Server-rendered SVG already attached — nothing to compile.
        if (typeof diagram.svg_cache === 'string' && diagram.svg_cache.trim()) return;
        const code = diagram.code;
        if (typeof code !== 'string' || !code.includes('\\begin{tikzpicture}')) return;
        jobs.push({ tikzCode: code, worksheetId: activeWorksheet.id });
      });
    });

    if (jobs.length === 0) return undefined;

    const controller = new AbortController();
    // Defer the queue start a beat so the initial paint and worksheet
    // hydration finish before TikZJax starts contending for the main
    // thread. The renders themselves run in iframes, but srcdoc parsing
    // still hits the parser.
    const startTimer = window.setTimeout(() => {
      prerenderTikzList(jobs, { signal: controller.signal })
        .catch(() => { /* prerender failures are silent; tiles still work */ });
    }, 600);

    return () => {
      window.clearTimeout(startTimer);
      try { controller.abort(); } catch (_e) { /* ignore */ }
    };
  }, [activeWorksheet?.id, activeWorksheet?._reloadTimestamp, activeWorksheet?.json_data]);

  // Always use the in-page render path — iframe TikZJax is disabled (see
  // shouldRenderTikzInIframe above). Cached diagrams are inlined as SVG and
  // uncached ones become "View Diagram" buttons; either way the worksheet
  // view never compiles TikZ in place.
  React.useEffect(() => {
    const currentKey = worksheetRenderKey;
    if (
      worksheetContentRef.current
      && sanitizedWorksheetHtmlContent
      && htmlSetRef.current !== currentKey
    ) {
      setIsContentReady(false); // Mark as not ready while loading
      // The processed HTML already has cached scripts replaced with SVG and
      // uncached scripts replaced with view buttons; that's the canonical
      // body the worksheet should render now.
      const cachedHtml = tikzCachedWorksheetContent.html || sanitizedWorksheetHtmlContent;
      const labeledHtml = tikzCachedWorksheetContent.html
        ? cachedHtml
        : labelDiagramsInHtml(cachedHtml);
      // Mobile-safe HTML rewrite still applies on small screens (it limits
      // image sizes etc.) — it doesn't need to strip TikZ scripts anymore
      // because the processed HTML has none.
      const baseContent = isMobileDevice && hasTikZ
        ? getMobileSafeHtmlContent(labeledHtml)
        : labeledHtml;
      setIsContentReady(false); // Mark as not ready while loading
      const processedContent = `<style>
        /*
         * Worksheet content always renders in light mode even when the app UI
         * is dark.  Scope the CSS custom properties used by the worksheet HTML
         * template (--text, --muted, --accent, --border) directly on the
         * .worksheet-content element so they win over the .dark overrides that
         * the app's global CSS sets on <html>.  CSS custom properties resolve
         * from the nearest ancestor that declares them, so these values
         * propagate to every descendant without needing !important.
         */
        .worksheet-content {
          --text: #111;
          --muted: #444;
          --accent: #0a66c2;
          --border: #ddd;
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #111;
          background: #fff;
        }
        .worksheet-content .marks { 
          color: #666; 
          font-weight: bold; 
          background: #f0f0f0;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 0.9em;
        }
        /* Style for radio buttons and labels */
        .answer-input label {
          display: flex;
          align-items: center;
          padding: 8px;
          margin: 4px 0;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s;
          color: #111;
        }
        .answer-input label:hover {
          background-color: #f3f4f6;
        }
        .answer-input input[type="radio"] {
          margin-right: 8px;
          width: 16px;
          height: 16px;
          cursor: pointer;
        }
        .answer-input input[type="radio"]:checked + span,
        .answer-input input[type="radio"]:checked ~ span {
          font-weight: 600;
          color: #2563eb;
        }
        /* Highlight selected option container */
        .answer-input label:has(input[type="radio"]:checked) {
          background-color: #dbeafe;
          border: 1px solid #3b82f6;
        }
        .diagram-placeholder {
          margin: 0.75rem 0;
          padding: 0.75rem;
          border-radius: 0.5rem;
          background: #eff6ff;
          border: 1px dashed #93c5fd;
          color: #1e3a8a;
          font-size: 0.9rem;
        }
        .math-toggle-wrapper {
          display: block;
          position: relative;
        }
        .math-toggle-btn {
          position: absolute;
          top: 6px;
          right: 6px;
          z-index: 1;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 1px solid #dee2e6;
          background: #f1f3f5;
          color: #495057;
          font-size: 14px;
          font-style: italic;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .math-toggle-btn:hover {
          background: #e9ecef;
          color: #2563eb;
        }
        .math-toggle-btn.active {
          background: #2563eb;
          color: #fff;
          border-color: transparent;
        }
        .math-toggle-field {
          display: block;
          width: 100%;
          min-height: 40px;
          padding: 8px 40px 8px 12px;
          font-size: 1rem;
          border: 1px solid #ced4da;
          border-radius: 6px;
          background: #fff;
        }
        .math-toggle-field:focus-within {
          outline: none;
          border-color: #2563eb;
        }
      </style>
      ${baseContent}`;
      
      worksheetContentRef.current.innerHTML = processedContent;
      htmlSetRef.current = currentKey;
      setIsContentReady(true); // Mark as ready
      console.log('HTML content set for worksheet:', activeWorksheet?.id);
    }
  }, [hasTikZ, sanitizedWorksheetHtmlContent, worksheetRenderKey, isMobileDevice, tikzCachedWorksheetContent.html]);
  
  const extractedEditableQuestions = useMemo(
    () => extractEditableQuestions(activeWorksheet),
    [activeWorksheet?.id, activeWorksheet?._reloadTimestamp, activeWorksheet?.json_data]
  );

  const hasStructuredQuestions = extractedEditableQuestions.length > 0;

  const handleToggleOneQuestionMode = () => {
    if (!enableOneQuestionToggle) return;
    if (!oneQuestionMode) {
      // Hydrate synchronously in the same React batch so OneQuestionAtATime
      // never mounts with an empty overrideQuestions list.
      setEditableQuestions(extractedEditableQuestions);
      setQuestionsSaveError('');
      setQuestionsSaveSuccess('');
    }
    setOneQuestionMode((prev) => !prev);
  };

  // When a parent (such as ChildDashboard's "Show All" view) disables the
  // built-in toggle, force the full worksheet renderer instead of mounting the
  // default one-question preview without a way back to "Show All".
  useEffect(() => {
    if (!enableOneQuestionToggle && oneQuestionMode) {
      setOneQuestionMode(false);
    }
  }, [enableOneQuestionToggle, oneQuestionMode]);

  // Re-apply the TikZ default whenever a different worksheet is loaded. Only
  // fires on id change so a user's in-session toggle is never overridden.
  useEffect(() => {
    if (!enableOneQuestionToggle) return;
    const html = activeWorksheet?.html_content || '';
    const hasTikZInHtml = html.includes('type="text/tikz"') || html.includes("type='text/tikz'");
    setOneQuestionMode(!hasTikZInHtml);
  }, [activeWorksheet?.id, enableOneQuestionToggle]);

  // Notify the parent whenever the view mode changes so it can adjust its own
  // UI (e.g. hide a "View Answer Key" button that is redundant in OneQuestion mode).
  useEffect(() => {
    onViewModeChange?.(oneQuestionMode);
  }, [oneQuestionMode, onViewModeChange]);

  // Keep editableQuestions in sync when the underlying worksheet changes while
  // we are already in one-question mode (e.g. after a save refresh).
  useEffect(() => {
    if (!oneQuestionMode) return;
    setEditableQuestions(extractedEditableQuestions);
    setQuestionsSaveError('');
    setQuestionsSaveSuccess('');
  }, [oneQuestionMode, extractedEditableQuestions]);

  // If the worksheet loses its structured questions, fall back to "Show All".
  useEffect(() => {
    if (oneQuestionMode && !hasStructuredQuestions) {
      setOneQuestionMode(false);
    }
  }, [oneQuestionMode, hasStructuredQuestions]);

  const handleEditableQuestionsChange = (updated) => {
    setEditableQuestions(Array.isArray(updated) ? updated : []);
  };

  const handleSaveEditableQuestions = async () => {
    if (!activeWorksheet?.id) return;
    if (!editableQuestions.length) {
      setQuestionsSaveError('At least one question is required.');
      return;
    }

    for (let i = 0; i < editableQuestions.length; i += 1) {
      const q = editableQuestions[i];
      if (!q.question_text || q.question_text.trim() === '') {
        setQuestionsSaveError(`Question ${i + 1}: Question text is required.`);
        return;
      }
      if (q.question_type === 'Multiple Choice') {
        if (!q.options || q.options.length < 2) {
          setQuestionsSaveError(`Question ${i + 1}: Provide at least two options.`);
          return;
        }
        if (q.options.some((opt) => !opt || opt.trim() === '')) {
          setQuestionsSaveError(`Question ${i + 1}: Remove empty options or fill them in.`);
          return;
        }
      }
      const answerStr = typeof q.correct_answer === 'string' ? q.correct_answer.trim() : '';
      if (!answerStr) {
        setQuestionsSaveError(`Question ${i + 1}: Correct answer is required.`);
        return;
      }
    }

    setQuestionsSaving(true);
    setQuestionsSaveError('');
    setQuestionsSaveSuccess('');

    try {
      await axios.put(`/worksheets/${activeWorksheet.id}`, {
        questions: editableQuestions,
        num_questions: editableQuestions.length
      });

      const refreshed = await axios.get(`/worksheets/${activeWorksheet.id}?t=${Date.now()}`);
      setLocalWorksheet(refreshed.data);
      if (onWorksheetUpdated) {
        onWorksheetUpdated(refreshed.data);
      }
      setQuestionsSaveSuccess('Worksheet updated successfully.');
    } catch (err) {
      console.error('Error saving worksheet questions:', err);
      setQuestionsSaveError(err.response?.data?.detail || 'Failed to save changes.');
    } finally {
      setQuestionsSaving(false);
    }
  };

  return (
    <div className={`enhanced-worksheet ${className}`}>
      {/* Worksheet Content */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h4 className="text-md font-medium text-gray-900">
            Worksheet{truncatedWorksheetTitle ? `: ${truncatedWorksheetTitle}` : ''}
            {/*{activeWorksheet?.enhanced && (
              <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                Enhanced
              </span>
            )}*/}
          </h4>
          {enableOneQuestionToggle && hasStructuredQuestions && (
            <button
              type="button"
              onClick={handleToggleOneQuestionMode}
              className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center ${
                oneQuestionMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              aria-pressed={oneQuestionMode}
            >
              <span className="mr-2">{oneQuestionMode ? '📄' : '📱'}</span>
              {oneQuestionMode ? 'Show All' : 'One Question at a Time'}
            </button>
          )}
        </div>

        {oneQuestionMode && (
          <div className="space-y-3 mb-4">
            {canEditQuestions && (
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800">
                Use <span className="font-semibold">Edit</span> on any question to update the text, options, hint, or correct answer. Click <span className="font-semibold">Save Changes</span> to persist your updates.
              </div>
            )}
            {questionsSaveError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {questionsSaveError}
              </div>
            )}
            {questionsSaveSuccess && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
                {questionsSaveSuccess}
              </div>
            )}
            <OneQuestionAtATime
              worksheet={activeWorksheet}
              previewMode
              editable={canEditQuestions}
              overrideQuestions={editableQuestions}
              onQuestionsChange={handleEditableQuestionsChange}
              onDeleteQuestion={handleEditableQuestionsChange}
              hintEnabled
            />
            {canEditQuestions && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveEditableQuestions}
                  disabled={questionsSaving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                >
                  {questionsSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        )}

        {/*
          Keep the worksheet content mounted at all times and just hide it when
          in one-question mode. Conditionally unmounting it caused the iframe /
          content div to lose its rendered HTML on toggle-back, because the
          existing HTML-writing effects depend on `htmlSetRef.current` which
          persists across re-mounts.
        */}
        {/* Worksheet Show All content — always rendered on a white background so
            the HTML content (which uses hardcoded light colors) stays readable
            even when the surrounding UI is in dark mode. colorScheme:'light'
            prevents the browser from cascading dark-mode text colors into this
            subtree. */}
        <div
          style={{ display: oneQuestionMode ? 'none' : 'block', colorScheme: 'light', color: '#111111' }}
          className="bg-white rounded-lg"
        >
          {/* Show loading state while determining content type */}
          {hasTikZ === null ? (
            <div className="border rounded-lg p-4 bg-gray-50 min-h-[400px] flex items-center justify-center">
              <div className="text-gray-500">Loading worksheet...</div>
            </div>
          ) : (
            /* Conditional rendering: iframe for TikZ content, regular div for non-TikZ */
            shouldRenderTikzInIframe ? (
              <iframe
                ref={iframeRef}
                className="border rounded-lg overflow-auto worksheet-content"
                style={{
                  width: '100%',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  minHeight: '400px',
                  overflow: 'auto',
                  backgroundColor: '#ffffff',
                  colorScheme: 'light',
                  display: isContentReady ? 'block' : 'none'
                }}
                title="Worksheet Content with Diagrams"
                data-testid="worksheet-preview"
              />
            ) : (
              <div
                ref={worksheetContentRef}
                onClick={handleWorksheetClick}
                className="border rounded-lg p-4 overflow-auto max-h-96 worksheet-content"
                style={{
                  backgroundColor: '#ffffff',
                  color: '#111111',
                  colorScheme: 'light',
                  display: isContentReady ? 'block' : 'none'
                }}
                data-testid="worksheet-preview"
              />
            )
          )}

          {/* Diagram viewer modal removed: clicking the embedded "View
              Diagram" button now triggers a background render through
              handleWorksheetClick. The cached SVG is then inlined into the
              worksheet HTML directly. */}

          {/* Show loading indicator if content is being prepared */}
          {hasTikZ !== null && !isContentReady && (
            <div className="border rounded-lg p-4 bg-gray-50 min-h-[400px] flex items-center justify-center">
              <div className="text-gray-500">Preparing content...</div>
            </div>
          )}
        </div>
      </div>

      {diagramEditor.isOpen && canEditDiagramEffective && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Edit Diagram</h2>
                <p className="text-sm text-gray-600 mt-1">Question {diagramEditor.questionId || '-'}</p>
              </div>
              <button
                onClick={closeDiagramEditor}
                className="text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-gray-200 transition-all"
                aria-label="Close"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setDiagramEditor((prev) => ({ ...prev, editorMode: 'visual' }))}
                    className={`px-3 py-1.5 text-sm rounded-full ${diagramEditor.editorMode === 'visual' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Visual
                  </button>
                  <button
                    onClick={() => setDiagramEditor((prev) => ({ ...prev, editorMode: 'svg' }))}
                    className={`px-3 py-1.5 text-sm rounded-full ${diagramEditor.editorMode === 'svg' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Edit SVG
                  </button>
                  <button
                    onClick={() => setDiagramEditor((prev) => ({ ...prev, editorMode: 'tikz' }))}
                    className={`px-3 py-1.5 text-sm rounded-full ${diagramEditor.editorMode === 'tikz' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Edit TikZ
                  </button>
                  <button
                    onClick={() => setDiagramEditor((prev) => ({ ...prev, excalidrawOpen: true }))}
                    className="px-3 py-1.5 text-sm rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 border border-violet-200"
                  >
                    ✏ Sketch Diagram
                  </button>
                </div>

                {diagramEditor.editorMode === 'visual' ? (
                  <Suspense fallback={<div className="text-sm text-gray-500">Loading visual editor...</div>}>
                    <DiagramVisualEditor
                      key={`${diagramEditor.questionId || 'diagram'}-${diagramEditor.isOpen ? 'open' : 'closed'}`}
                      svgCode={diagramEditor.svgCode}
                      onSvgCodeChange={(nextSvgCode) => setDiagramEditor((prev) => ({ ...prev, svgCode: nextSvgCode }))}
                    />
                  </Suspense>
                ) : diagramEditor.editorMode === 'svg' ? (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">SVG Markup</label>
                    <textarea
                      value={diagramEditor.svgCode}
                      onChange={(e) => setDiagramEditor((prev) => ({ ...prev, svgCode: e.target.value }))}
                      className="w-full min-h-[320px] border border-gray-300 rounded-lg p-3 font-mono text-xs"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">TikZ Code</label>
                    <textarea
                      value={diagramEditor.tikzCode}
                      onChange={(e) => setDiagramEditor((prev) => ({ ...prev, tikzCode: e.target.value }))}
                      className="w-full min-h-[320px] border border-gray-300 rounded-lg p-3 font-mono text-xs"
                    />
                    <button
                      onClick={handleRegenerateSvg}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      type="button"
                    >
                      Regenerate SVG from TikZ
                    </button>
                  </div>
                )}

                {diagramEditorError && (
                  <div className="text-sm text-red-600">{diagramEditorError}</div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Diagram Preview</label>
                {diagramEditor.svgCode ? (
                  <iframe
                    title="diagram-svg-preview"
                    className="w-full min-h-[320px] border border-gray-200 rounded-lg bg-gray-50"
                    srcDoc={svgPreviewDoc}
                    sandbox=""
                  />
                ) : (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 min-h-[320px] overflow-auto">
                    <p className="text-gray-500 text-sm">No SVG to preview.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeDiagramEditor}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDiagram}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md disabled:opacity-50"
                type="button"
                disabled={isSavingDiagram}
              >
                {isSavingDiagram
                  ? 'Saving...'
                  : (diagramEditor.editorMode === 'tikz')
                    ? 'Save TikZ'
                    : 'Save SVG'}
              </button>
            </div>
          </div>
        </div>
      )}

      {diagramEditor.excalidrawOpen && (
        <Suspense fallback={null}>
          <VideoExcalidrawModal
            slide={{ title: `Question ${diagramEditor.questionId || ''}`, excalidraw_elements: diagramEditor.excalidrawElements }}
            onSave={(svgString, elements) => {
              setDiagramEditor((prev) => ({
                ...prev,
                svgCode: svgString,
                excalidrawElements: elements,
                excalidrawOpen: false,
                editorMode: 'svg'
              }));
            }}
            onClose={() => setDiagramEditor((prev) => ({ ...prev, excalidrawOpen: false }))}
          />
        </Suspense>
      )}

      {/* Answer Key */}
      {showAnswerKey && (activeWorksheet?.answer_key || (activeWorksheet?.is_olympiad && activeWorksheet?.json_data)) && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-md font-medium text-gray-900">Answer Key</h4>
            <button
              onClick={() => {
                // Toggle answer key visibility (similar to "Show All Answers" functionality)
                const answerKeyElement = document.querySelector('.answer-key-content');
                if (answerKeyElement) {
                  answerKeyElement.classList.toggle('hidden');
                }
              }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Show/Hide Answers
            </button>
          </div>
          <div className="answer-key-content border rounded-lg p-4 bg-green-50 overflow-auto max-h-96">
            {activeWorksheet?.answer_key ? (
              <div dangerouslySetInnerHTML={{ __html: activeWorksheet.answer_key }} />
            ) : activeWorksheet?.is_olympiad && activeWorksheet?.json_data ? (
              <div className="space-y-4">
                {(() => {
                  const jsonData = typeof activeWorksheet.json_data === 'string' ? JSON.parse(activeWorksheet.json_data) : activeWorksheet.json_data;
                  return jsonData.answer_key?.map((answer, idx) => (
                    <div key={idx} className="border-b pb-3">
                      <p className="font-medium text-gray-900">Q{answer.question_number}: {answer.answer_text}</p>
                      <p className="text-sm text-gray-700 mt-1">{answer.answer_explanation}</p>
                      <p className="text-xs text-gray-500 mt-1">Concept: {answer.concept}</p>
                    </div>
                  ));
                })()}
              </div>
            ) : null}
          </div>
        </div>
      )}
      
      {/* JSON Data Display (for debugging, for enhanced or olympiad worksheets) */}
      {/*{(activeWorksheet?.enhanced || activeWorksheet?.is_olympiad) && activeWorksheet?.json_data && process.env.NODE_ENV === 'development' && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-gray-600">
            View JSON Data (Debug){activeWorksheet?.is_olympiad ? ' - Olympiad' : ''}
          </summary>
          <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-96">
            {JSON.stringify(typeof activeWorksheet.json_data === 'string' ? JSON.parse(activeWorksheet.json_data) : activeWorksheet.json_data, null, 2)}
          </pre>
        </details>
      )}*/}
    </div>
  );
};

export default EnhancedWorksheetDisplay;